import React, { useRef, useState } from 'react';
import { Box, Flex, Image, Switch, Text, useColorModeValue } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useReduxDispatch } from '../../../app/store';
import displayIconDark from '../../../assets/images/display_icon_dark.svg';
import displayIconLight from '../../../assets/images/display_icon.svg';
import energyRatesIconDark from '../../../assets/images/energy_rates_dark.svg';
import energyRatesIconLight from '../../../assets/images/energy_rates.svg';
import languageIconDark from '../../../assets/images/language_icon_dark.svg';
import languageIconLight from '../../../assets/images/language_icon.svg';
import familyIconDark from '../../../assets/images/person_dark.svg';
import familyIconLight from '../../../assets/images/person_light.svg';
import privacyIconDark from '../../../assets/images/privacy_dark.svg';
import privacyIconLight from '../../../assets/images/privacy_light.svg';
import scheduleIconDark from '../../../assets/images/schedule_dark.svg';
import scheduleIconLight from '../../../assets/images/schedule.svg';
import smartAmpIconDark from '../../../assets/images/smart_amp_dark.svg';
import smartAmpIconLight from '../../../assets/images/smart_amp_light.svg';
import smartPlugIconDark from '../../../assets/images/smart_plug_dark.svg';
import smartPlugIconLight from '../../../assets/images/smart_plug_light.svg';
import wifiIconDark from '../../../assets/images/wifi_dark.svg';
import wifiIconLight from '../../../assets/images/wifi.svg';
import { SEHomeCard } from '../../../common/components/SEHomeCard';
import { SlidingAnimationPageBase } from '../../../common/components/SlidingAnimationPageBase';
import { IS_RUNNING_CYPRESS_TESTS } from '../../../common/constants';
import { trackDisplayConfigOpened, trackEnergyRatesOpened } from '../../../utils/common/analytics';
import {
  trackDynatraceDisplayConfigOpened,
  trackDynatraceEnergyRatesOpened,
  trackDynatraceScheduleSettingsOpened,
  trackDynatraceWifiConfigOpened,
} from '../../../utils/native/dynatrace-events';
import {
  resetEvChargerData,
  selectChargeSimulationType,
  selectEvseType,
} from '../../ev-charger/dashboard/evChargerSlice';
import { SimulationDeveloperTool } from '../../ev-charger/dashboard/SimulationDeveloperTool';
import { EvseApReadinessSettings } from '../../ev-charger/device-setup/EvseApReadinessSettings';
import { selectEvChargers, selectIsSmartAmpCapable } from '../../site/siteSlice';
import { selectUser } from '../../user/userSlice';
import { AccountMenuButton } from '../AccountMenuButton';
import { toggleFeatureFlagsModal } from '../featureFlagSlice';

export function SettingsHome() {
  const navigate = useNavigate();
  const dispatch = useReduxDispatch();
  const user = useSelector(selectUser);
  const isSmartAmpCapableFromSite = useSelector(selectIsSmartAmpCapable);
  const evseType = useSelector(selectEvseType);
  const chargeSimulationType = useSelector(selectChargeSimulationType);
  const isSmartAmpCapable = chargeSimulationType !== 'None' || isSmartAmpCapableFromSite;
  // Charger-specific settings are only relevant once a charger exists (SHN-2999).
  const hasCharger = useSelector(selectEvChargers).length > 0;
  const { t } = useTranslation();
  const isMouseDown = useRef(false);
  const [isDeveloperToolsSwitchVisible, setDeveloperToolsSwitchVisible] = useState(false);
  const [shouldEnableDeveloperTools, setDeveloperToolsEnabled] = useState(
    localStorage.getItem('developerToolsEnabled') === 'true'
  );

  const scheduleIcon = useColorModeValue(scheduleIconLight, scheduleIconDark);
  const smartAmpIcon = useColorModeValue(smartAmpIconLight, smartAmpIconDark);
  const smartPlugIcon = useColorModeValue(smartPlugIconLight, smartPlugIconDark);
  const wifiIcon = useColorModeValue(wifiIconLight, wifiIconDark);
  const energyRatesIcon = useColorModeValue(energyRatesIconLight, energyRatesIconDark);
  const familyIcon = useColorModeValue(familyIconLight, familyIconDark);
  const displayIcon = useColorModeValue(displayIconLight, displayIconDark);
  const languageIcon = useColorModeValue(languageIconLight, languageIconDark);
  const privacyIcon = useColorModeValue(privacyIconLight, privacyIconDark);

  function handleSetDeveloperToolsEnabled(isEnabled: boolean) {
    setDeveloperToolsEnabled(isEnabled);
    localStorage.setItem('developerToolsEnabled', isEnabled.toString());
    if (!isEnabled) {
      dispatch(resetEvChargerData());
    }
  }

  function handleMouseDown() {
    isMouseDown.current = true;
    setTimeout(() => {
      if (isMouseDown.current) {
        setDeveloperToolsSwitchVisible(true);
      }
    }, 3000);
  }

  function handleMouseUp() {
    isMouseDown.current = false;
  }

  return (
    <SlidingAnimationPageBase title={t('Account.settings')} backURL={`../../dashboard?direction=back`}>
      <Box px={5}>
        <Text pt={5} pb={2} fontSize="sm" color="gray.500">
          {t('Account.settings').toUpperCase()}
        </Text>
        <SEHomeCard p={0} borderTopLeftRadius="5px">
          {hasCharger && (
            <AccountMenuButton
              data-testid="charging-schedule-button"
              onClick={() => {
                trackDynatraceScheduleSettingsOpened();
                navigate(
                  '../../../home/device-charging-schedule?direction=forward&backURL=../../account/settings/home'
                );
              }}
            >
              <Flex align="center">
                <Image src={scheduleIcon} w="30px" h="30px" mr={3} flexShrink={0} />
                <Text fontSize={16} fontWeight="normal">
                  {t('Account.charging schedule')}
                </Text>
              </Flex>
            </AccountMenuButton>
          )}

          {hasCharger && isSmartAmpCapable && (
            <AccountMenuButton
              data-testid="smart-device-button"
              onClick={() =>
                navigate(
                  `../../../home/${
                    evseType === 'SmartAmp' ? 'smart-amp' : 'smart-plug'
                  }?direction=forward&backURL=../../account/settings/home`
                )
              }
            >
              <Flex align="center">
                <Image
                  src={evseType === 'SmartAmp' ? smartAmpIcon : smartPlugIcon}
                  w="30px"
                  h="30px"
                  mr={3}
                  flexShrink={0}
                />
                <Text fontSize={16} fontWeight="normal">
                  {evseType === 'SmartAmp' ? t('Account.smart amp') : t('Account.smart plug')}
                </Text>
              </Flex>
            </AccountMenuButton>
          )}

          {hasCharger && (
            <AccountMenuButton
              data-testid="wifi-settings-button"
              onClick={() => {
                trackDynatraceWifiConfigOpened();
                navigate(
                  '../../../home/device-wifi-configuration?direction=forward&backURL=../../account/settings/home'
                );
              }}
            >
              <Flex align="center">
                <Image src={wifiIcon} w="30px" h="30px" mr={3} flexShrink={0} />
                <Text fontSize={16} fontWeight="normal">
                  {t('Account.wifi settings')}
                </Text>
              </Flex>
            </AccountMenuButton>
          )}

          {hasCharger && (
            <AccountMenuButton
              data-testid="energy-rates-button"
              onClick={() => {
                trackEnergyRatesOpened();
                trackDynatraceEnergyRatesOpened();
                navigate('../../tariff/home?direction=forward&backURL=../../settings/home');
              }}
            >
              <Flex align="center">
                <Image src={energyRatesIcon} w="30px" h="30px" mr={3} flexShrink={0} />
                <Text fontSize={16} fontWeight="normal">
                  {t('Energy Rates.energy rates')}
                </Text>
              </Flex>
            </AccountMenuButton>
          )}

          <AccountMenuButton data-testid="language-button" onClick={() => navigate('../language?direction=forward')}>
            <Flex align="center">
              <Image src={languageIcon} w="30px" h="30px" mr={3} flexShrink={0} />
              <Text fontSize={16} fontWeight="normal">
                {t('Common.language')}
              </Text>
            </Flex>
          </AccountMenuButton>

          <AccountMenuButton
            data-testid="display-button"
            onClick={() => {
              trackDisplayConfigOpened();
              trackDynatraceDisplayConfigOpened();
              navigate('../display?direction=forward');
            }}
            includeBottomDivider={true}
          >
            <Flex align="center">
              <Image src={displayIcon} w="30px" h="30px" mr={3} flexShrink={0} />
              <Text
                fontSize={16}
                fontWeight="normal"
                onTouchStart={handleMouseDown}
                onTouchEnd={handleMouseUp}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                {t('Account.display')}
              </Text>
            </Flex>
          </AccountMenuButton>
          <AccountMenuButton
            data-testid="family-members-button"
            onClick={() => navigate('../family-members?direction=forward')}
          >
            <Flex align="center">
              <Image src={familyIcon} w="30px" h="30px" mr={3} flexShrink={0} />
              <Text fontSize={16} fontWeight="normal">
                {t('Account.family members')}
              </Text>
            </Flex>
          </AccountMenuButton>

          <AccountMenuButton
            data-testid="privacy-settings-button"
            onClick={() => navigate('../privacy?direction=forward&backURL=../home')}
            includeBottomDivider={false}
          >
            <Flex align="center">
              <Image src={privacyIcon} w="30px" h="30px" mr={3} flexShrink={0} />
              <Text fontSize={16} fontWeight="normal">
                {t('Account.privacy settings')}
              </Text>
            </Flex>
          </AccountMenuButton>
        </SEHomeCard>

        {(user.role === 'SUPER_ADMIN' || user.role === 'STAFF' || IS_RUNNING_CYPRESS_TESTS) && (
          <>
            <Text pt={5} pb={2} fontSize="sm" color="gray.500">
              TESTING / DEVELOPER
            </Text>

            {user.role === 'SUPER_ADMIN' && isDeveloperToolsSwitchVisible && (
              <Flex mt={2} mb={3} align={'center'} justify="space-between" px={4}>
                <Text fontSize={18} fontWeight={550}>
                  Enable Developer Tools
                </Text>
                <Switch
                  isChecked={shouldEnableDeveloperTools}
                  onChange={(event) => handleSetDeveloperToolsEnabled(event.target.checked)}
                  size="lg"
                  variant="solarGreen"
                />
              </Flex>
            )}

            <SEHomeCard pr={0} pl={2} py={0} mb={10} borderTopLeftRadius="5px">
              <SimulationDeveloperTool />
              <EvseApReadinessSettings />
              <AccountMenuButton
                data-testid="experimental-features"
                cursor="pointer"
                onClick={() => {
                  dispatch(toggleFeatureFlagsModal(true));
                }}
                includeBottomDivider={false}
              >
                <Text fontSize={16} fontWeight="normal" overflow="auto">
                  {t('Feature Flag.title')}
                </Text>
              </AccountMenuButton>
            </SEHomeCard>
          </>
        )}
      </Box>
    </SlidingAnimationPageBase>
  );
}
